import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Cluster } from '../../../core/models/cluster';
import { ClusterService } from '../../../core/services/cluster-service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ClusterFormComponent } from './cluster-form/cluster-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';


@Component({
  selector: 'em-cluster',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective
  ],
  templateUrl: './cluster.component.html',
  styleUrl: './cluster.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class ClusterComponent {
  clusters!: Cluster[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;
  constructor(
    private clusterService: ClusterService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllClusters();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.cluster.addCluster.title',
        'layout.configurations.cluster.addCluster.successMessage.title',
        'layout.configurations.cluster.addCluster.successMessage.detail',
        'layout.configurations.cluster.editCluster.title',
        'layout.configurations.cluster.editCluster.successMessage.title',
        'layout.configurations.cluster.editCluster.successMessage.detail',
        'layout.configurations.cluster.deleteCluster.title',
        'layout.configurations.cluster.deleteCluster.successMessage.title',
        'layout.configurations.cluster.deleteCluster.successMessage.detail',
        'layout.configurations.cluster.deleteCluster.message',
        'layout.configurations.cluster.deleteCluster.yes',
        'layout.configurations.cluster.deleteCluster.no',
        'layout.configurations.cluster.deleteCluster.errorMessage.title',
        'layout.configurations.cluster.deleteCluster.errorMessage.detail',
        'layout.configurations.cluster.deleteCluster.infoMessage.title',
        'layout.configurations.cluster.deleteCluster.infoMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }
  getAllClusters() {
    this.clusterService.getClusters().subscribe((data) => (this.clusters = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(ClusterFormComponent, {
      header: this.translations['layout.configurations.cluster.addCluster.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((cluster) => {
      if (cluster) {
        this.getAllClusters();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.cluster.addCluster.successMessage.title'],
          detail: this.translations['layout.configurations.cluster.addCluster.successMessage.detail'],
        });
      }
    });
  }

  onEdit(cluster: Cluster) {
    this.ref = this.dialogService.open(ClusterFormComponent, {
      header: this.translations['layout.configurations.cluster.editCluster.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: cluster,
    });

    this.ref.onClose.subscribe((result) => {
    if (!result) return;

    if (result.type === 'updated') {
        this.getAllClusters();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.cluster.editCluster.successMessage.title'],
          detail: this.translations['layout.configurations.cluster.editCluster.successMessage.detail'],
        });
      }
       else if (result.type === 'no-change') {
        this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
       }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.cluster.deleteCluster.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.cluster.deleteCluster.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.cluster.deleteCluster.yes'],
      rejectLabel: this.translations['layout.configurations.cluster.deleteCluster.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteCluster(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.cluster.deleteCluster.infoMessage.title'],
          detail: this.translations['layout.configurations.cluster.deleteCluster.infoMessage.detail'],
          life: 3000,
        });
      },
    });
  }

  deleteCluster(id: number) {
    this.clusterService.deleteCluster(id).subscribe({
      next: () => {
        this.getAllClusters();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.cluster.deleteCluster.successMessage.title'],
          detail: this.translations['layout.configurations.cluster.deleteCluster.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting cluster: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.cluster.deleteCluster.errorMessage.title'],
          detail: this.translations['layout.configurations.cluster.deleteCluster.errorMessage.detail'],
        });
      },
    });
  }
}
